import { useEffect, useMemo } from "react"
import styles from "../styles/dashboard.module.scss"
import { TripList } from "../components/organisms/TripList"
import { CruiseList } from "../components/organisms/CruiseList"
import { Navigator } from "../components/organisms/Navigator"
import { _rootStore, useStores } from "../models/helpers/useStores"
import { RoomList } from "../components/organisms/RoomList"
import { observer } from "mobx-react-lite"
import { DashboardHeader } from "../components/molecules/DashboardHeader"
import { save } from "../utils/storage"
import BookedCruise from "../components/organisms/BookedCruise"
import { CruiseDetails } from "../components/organisms/CruiseDetails"
import classNames from "classnames"
import { makeAutoObservable } from "mobx"
import { cleanupNotifications, initializeNotifications } from "../services/notifications.ts"
import { useSearchParams } from "react-router-dom"
import { getTrip, updateTrip } from "../services/firestore/trips.ts"
import { Popover } from "react-tiny-popover"
import CaptainHatIcon from "../components/assets/icons/CaptainHatIcon.tsx"
import DocumentationIcon from "../components/assets/icons/DocumentationIcon.tsx"
import HelpHeadphonesIcon from "../components/assets/icons/HelpHeadphonesIcon.tsx"
import Intercom from "@intercom/messenger-js-sdk"
import Config from "../config"
import { fetchUserByUid, updateUser } from "../services/firestore/user.ts"
import { useIsTabActive } from "../hooks/useIsTabActive.ts"
import { saveUserDeviceToken } from "../services/firebase-functions/userDeviceTokens.ts"
import { PaymentRequestDetails } from "../components/organisms/PaymentRequestDetails/index.tsx"
import {
  activeSubscriptionStatuses,
  fetchStripeCustomerByUserId,
  findLatestUnpaidInvoice,
  isSubscriptionEnabled,
  onStripeSubscription,
  terminatedSubscriptionStatuses,
} from "../services/firestore/billing.ts"
import { StripeSubscriptionModel } from "@cruisen/react-shared"
import { StripeDiscount } from "@cruisen/contract"
import { ModalWrapper } from "../components/templates/ModalWrapper/index.tsx"
import { SubscriptionModal } from "../components/organisms/SubscriptionModal/index.tsx"
import { InactiveSubscriptionModal } from "../components/modals/InactiveSubscriptionModal/index.tsx"
import { EmptyCruiseList } from "../components/organisms/EmptyCruiseList/index.tsx"
import { AccountModalTab } from "../components/organisms/TripList/modals/AccountModal/index.tsx"

class DashboardStore {
  showPopover: boolean
  isCruiseBookingOpen: boolean
  isHelpPopoverOpen: boolean
  headerBar: string | null
  defaultAccountModalTab: AccountModalTab | undefined

  constructor() {
    this.isCruiseBookingOpen = false
    // Hide the popover by default, since we have Intercom now
    this.showPopover = false
    this.isHelpPopoverOpen = false
    this.headerBar = null
    this.defaultAccountModalTab = undefined

    makeAutoObservable(this)
  }

  setHeaderBar(value: string | null) {
    this.headerBar = value
  }

  setIsCruiseBookingOpen(value: boolean) {
    this.isCruiseBookingOpen = value
  }

  toggleHelpPopover() {
    this.isHelpPopoverOpen = !this.isHelpPopoverOpen
  }

  setDefaultAccountModalTab(tab: AccountModalTab | undefined) {
    this.defaultAccountModalTab = tab
  }
}

const helpLinks = [
  {
    id: "cruisen-community",
    icon: <CaptainHatIcon />,
    text: "Cruisen Community",
    url: Config.slackCommunityInviteLink,
  },
  {
    id: "documentation",
    icon: <DocumentationIcon />,
    text: "Documentation",
    url: Config.appDocumentationLink,
  },
  {
    id: "get-help",
    icon: <HelpHeadphonesIcon />,
    text: "Get help",
    url: Config.appGetHelpLink,
  },
]

const Dashboard = observer(function Dashboard() {
  const rootStore = useStores()
  const [searchParams, setSearchParams] = useSearchParams()
  const isTabActive = useIsTabActive()
  const chosenCruiseDetails = rootStore.cruiseDetailsPlaceholderData?.id
  const paymentRequest = rootStore.paymentRequest
  const store = useMemo(() => new DashboardStore(), [])

  useEffect(() => {
    rootStore.setProp(
      "isCruisenDeveloper",
      rootStore.loggedInUser?.email?.endsWith("@cruisen.com") ||
        rootStore.loggedInUser?.email === "integration-testing@zapier.com",
    )
  }, [rootStore.loggedInUser?.id])

  useEffect(() => {
    if (!rootStore.loggedInUser) return

    return initializeNotifications()
  }, [rootStore.loggedInUser?.id])

  useEffect(() => {
    if (!rootStore.loggedInUser) return

    const handleTabClose = async () => {
      saveUserDeviceToken({ disconnected: true }).catch((error) =>
        console.error("user device token disconnect on tab close failure", {
          userId: rootStore.loggedInUser.id,
          error,
        }),
      )
    }

    window.addEventListener("beforeunload", handleTabClose)

    return () => {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [rootStore.loggedInUser?.id])

  useEffect(() => {
    if (!rootStore.loggedInUser?.id || rootStore.isCruisenDeveloper || !isSubscriptionEnabled())
      return

    return onStripeSubscription(rootStore.loggedInUser.id, async (stripeSubscription) => {
      if (!stripeSubscription) {
        rootStore.setProp("visibleModal", "startSubscription")
        return
      }

      const user = await fetchUserByUid(rootStore.loggedInUser.id)
      const customer = await fetchStripeCustomerByUserId(rootStore.loggedInUser.id)

      let discount: StripeDiscount | null = null
      if (typeof stripeSubscription.items?.[0]?.discounts?.[0] === "object") {
        discount = stripeSubscription.items[0].discounts[0]
      } else if (typeof stripeSubscription.discount === "object") {
        discount = stripeSubscription.discount
      }

      // stripe store prices in cents
      const price = stripeSubscription.items?.[0]?.price?.unit_amount / 100
      const discountAmountOff = discount?.coupon?.amount_off
        ? discount?.coupon?.amount_off / 100
        : 0
      const discountPercentOff = discount?.coupon?.percent_off ?? 0

      let discountPrice: number
      if (discountAmountOff) {
        discountPrice = price - discountAmountOff
      } else if (discountPercentOff) {
        discountPrice = (price / 100) * (100 - discountPercentOff)
      }

      const latestUnpaidInvoice =
        stripeSubscription.status == "past_due"
          ? await findLatestUnpaidInvoice(user.id, stripeSubscription.id)
          : null

      rootStore.setProp(
        "stripeSubscription",
        StripeSubscriptionModel.create({
          id: stripeSubscription.id,
          isEarlyAdopter: !!discount?.coupon?.metadata?.maxUserRegistrationDate,
          name: stripeSubscription.items[0].plan.nickname || "",
          status: stripeSubscription.status,
          price,
          defaultPaymentMethodId: stripeSubscription.default_payment_method,
          currency: stripeSubscription.items?.[0]?.price?.currency,
          created: stripeSubscription.created?.toDate()?.valueOf(),
          currentPeriodStart: stripeSubscription.current_period_start?.toDate()?.valueOf(),
          currentPeriodEnd: stripeSubscription.current_period_end?.toDate()?.valueOf(),
          cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
          endedAt: stripeSubscription.ended_at?.toDate()?.valueOf(),
          cancelAt: stripeSubscription.cancel_at?.toDate()?.valueOf(),
          trialStart: stripeSubscription.trial_start?.toDate()?.valueOf(),
          trialEnd: stripeSubscription.trial_end?.toDate()?.valueOf(),
          discountDuration: discount?.coupon?.duration,
          discountDurationInMonths: discount?.coupon?.duration_in_months,
          discountStart: discount?.start ? discount?.start * 1000 : null, // convert to milliseconds
          discountEnd: discount?.end ? discount?.end * 1000 : null, // convert to milliseconds
          discountPrice,
          discountAmountOff,
          discountPercentOff,
          unpaidInvoiceAmount: latestUnpaidInvoice?.amount_due,
          unpaidInvoiceNextPaymentAttempt: latestUnpaidInvoice?.next_payment_attempt
            ? latestUnpaidInvoice?.next_payment_attempt * 1000
            : null,
          // stripe store balance in cents
          balance: customer?.balance ? customer.balance / 100 : 0,
        }),
      )

      console.log("stripe subscription", {
        stripeSubscription: { ...rootStore.stripeSubscription },
      })

      if (terminatedSubscriptionStatuses.includes(stripeSubscription.status)) {
        rootStore.setProp("visibleModal", "startSubscription")
        return
      }

      if (!activeSubscriptionStatuses.includes(stripeSubscription.status)) {
        rootStore.setProp("visibleModal", "inactiveSubscription")
        return
      }

      if (stripeSubscription.status === "trialing" && !stripeSubscription.default_payment_method) {
        store.setHeaderBar("trialingBar")
      } else if (stripeSubscription.status === "past_due") {
        store.setHeaderBar("pastDueBar")
      } else {
        store.setHeaderBar(null)
      }
    })
  }, [rootStore.loggedInUser?.id])

  useEffect(() => {
    if (!rootStore.loggedInUser) return

    if (isTabActive) {
      saveUserDeviceToken({ trackOnline: true }).catch((error) =>
        console.error("user device token activity save failure", {
          userId: rootStore.loggedInUser.id,
          error,
        }),
      )
    }

    // track token activity every 5 minutes
    const interval = setInterval(
      () => {
        if (isTabActive) {
          saveUserDeviceToken({ trackOnline: true }).catch((error) =>
            console.error("recurring user device token activity save failure", {
              userId: rootStore.loggedInUser.id,
              error,
            }),
          )
        }
      },
      5 * 60 * 1000,
    )

    // disconnect when tab is inactive for more than 5 minutes
    const timeoutId = setTimeout(
      () => {
        if (!isTabActive) {
          saveUserDeviceToken({ disconnected: true })
            .then(() => clearTimeout(timeoutId))
            .catch((error) =>
              console.error("user device token disconnect for inactive tab failure", {
                userId: rootStore.loggedInUser.id,
                error,
              }),
            )
        }
      },
      5 * 60 * 1000,
    )

    return () => {
      clearTimeout(timeoutId)
      clearInterval(interval)
    }
  }, [rootStore.loggedInUser?.id, isTabActive])

  useEffect(() => {
    if (!rootStore.activeTrip?.id) return
    save("lastSeenTripId", rootStore.activeTrip?.id)
    cleanupNotifications()
    setSearchParams({ tripId: rootStore.activeTrip?.id })
  }, [rootStore.activeTrip?.id])

  const updateUserLastActive = () => {
    updateUser(rootStore.loggedInUser.id, {
      lastActive: Date.now(),
      platform: "web",
    }).catch((error) => console.error("update user last active failed", error))
  }

  useEffect(() => {
    if (!rootStore.loggedInUser) return

    updateUserLastActive()

    const interval = setInterval(
      () => {
        if (!isTabActive) return

        updateUserLastActive()
      },
      10 * 60 * 1000,
    )

    return () => clearInterval(interval)
  }, [rootStore.loggedInUser?.id])

  useEffect(() => {
    const routeSource = searchParams.get("source")
    if (routeSource === "zapier") {
      store.setDefaultAccountModalTab("integrations")
      setSearchParams({})
    }

    const routeTripId = searchParams.get("tripId")
    if (!routeTripId) return
    const activeTripId = rootStore.activeTrip?.id

    if (routeTripId !== activeTripId) {
      getTrip(routeTripId)
        .then((routeTrip) => {
          if (routeTrip) {
            _rootStore.setProp("cruiseDetailsPlaceholderData", null)
            _rootStore.setProp("activeTrip", routeTrip)
          }
        })
        .catch((error) =>
          console.error("trip not found by route data", {
            error,
            routeTripId,
          }),
        )
    }
  }, [searchParams])

  const updateTripLastActive = () => {
    updateTrip(rootStore.activeTrip?.id, {
      lastActive: Date.now(),
    }).catch((error) => console.error("update trip last active failed", error))
  }

  useEffect(() => {
    updateTripLastActive()

    // track activity every 10 minutes
    const interval = setInterval(updateTripLastActive, 10 * 60 * 1000)

    return () => clearInterval(interval) // Clear interval on component unmount
  }, [])

  const areTripDetailsEmpty =
    rootStore.activeTrip?.months.length === 0 &&
    rootStore.activeTrip?.originPortGroups.length === 0 &&
    rootStore.activeTrip?.destinationRegions.length === 0

  // Setup and start Intercom
  if (Config.intercomAppId && Config.intercomAppId !== "-") {
    Intercom({
      app_id: Config.intercomAppId,
      user_id: rootStore.loggedInUser?.id,
      name: `${rootStore.loggedInUser?.firstName} ${rootStore.loggedInUser?.lastName}`,
      email: rootStore.loggedInUser?.email,
      created_at: rootStore.loggedInUser?.created
        ? Math.floor(rootStore.loggedInUser?.created / 1000)
        : undefined,
    })
  }

  return (
    <>
      <div className={styles.container}>
        <DashboardHeader headerBar={store.headerBar} areTripDetailsEmpty={areTripDetailsEmpty} />
        <div
          className={classNames(
            styles.dashboard,
            !rootStore.activeTrip ? styles.dashboardNoTrip : "",
          )}
        >
          <div className={styles.dashboardTripListCard}>
            <TripList defaultAccountModalTab={store.defaultAccountModalTab} />
          </div>
          {rootStore.activeTrip ? (
            <>
              <div className={styles.dashboardNavigator}>
                <Navigator />
              </div>
              <div
                className={classNames(
                  styles.dashboardCardMain,
                  chosenCruiseDetails ? "" : styles.borderRightRadius,
                )}
              >
                {areTripDetailsEmpty ? (
                  <EmptyCruiseList />
                ) : rootStore.activeTrip?.chosenTripSearchResultId ? (
                  <BookedCruise tripId={rootStore.activeTrip.id} />
                ) : (
                  <CruiseList />
                )}
              </div>
              {chosenCruiseDetails ? (
                <div className={styles.dashboardCruiseDetails}>
                  <CruiseDetails />
                </div>
              ) : paymentRequest ? (
                <div className={styles.dashboardCard}>
                  <PaymentRequestDetails paymentRequestId={paymentRequest} />
                </div>
              ) : (
                <div className={styles.dashboardCard}>
                  <RoomList />
                </div>
              )}
            </>
          ) : (
            <div className={styles.dashboardCard}>
              <div className={styles.createTripContainer}>
                <p className={styles.createTripTitle}>No trips found.</p>
                <p className={styles.createTripText}>
                  Create your first trip to start your journey with us.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {store.showPopover && (
        <Popover
          isOpen={store.isHelpPopoverOpen}
          onClickOutside={() => store.toggleHelpPopover()}
          padding={6}
          align="end"
          containerClassName={styles.helpPopoverContainer}
          content={
            <div className={styles.helpPopover}>
              {helpLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.helpLink}
                >
                  <div className={styles.helpLinkIconContainer}>{link.icon}</div>
                  <p className={styles.helpLinkText}>{link.text}</p>
                </a>
              ))}
            </div>
          }
        >
          <button className={styles.helpButton} onClick={() => store.toggleHelpPopover()}>
            ?
          </button>
        </Popover>
      )}
      {rootStore.visibleModal === "startSubscription" && (
        <ModalWrapper>
          <SubscriptionModal onClose={() => rootStore.setProp("visibleModal", null)} />
        </ModalWrapper>
      )}
      {rootStore.visibleModal === "inactiveSubscription" && (
        <ModalWrapper>
          <InactiveSubscriptionModal onClose={() => rootStore.setProp("visibleModal", null)} />
        </ModalWrapper>
      )}
    </>
  )
})

export default Dashboard
